import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Loader2 } from 'lucide-react';
import PlayerNameLink from '../PlayerNameLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Sorted leaderboard of players who consumed the most credits via promo +
 * playback combined. Highlights risky players (playback ratio > 50%).
 */
const TopCreditConsumersCard = ({ defaultPeriod = 'today' }) => {
    const [period, setPeriod] = useState(defaultPeriod);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        axios.get(`${API}/admin/recon/top-consumers`, { params: { period, limit: 10 } })
            .then(({ data }) => {
                if (!cancelled) setRows(data.rows || []);
            })
            .catch(() => toast.error('Failed to load top consumers'))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [period]);

    const PERIODS = [
        { id: 'today', label: 'Today' },
        { id: 'week',  label: '7d'   },
        { id: 'month', label: '30d'  },
    ];

    return (
        <div className="card-dark p-4" data-testid="top-credit-consumers-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-white text-base font-black flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-[#FFD700]" />
                    Top Credit Consumers
                </h3>
                <div className="flex gap-1 rounded-lg bg-white/5 p-1">
                    {PERIODS.map(p => (
                        <button
                            key={p.id}
                            onClick={() => setPeriod(p.id)}
                            className={`px-3 py-1 text-[11px] font-bold rounded-md transition-all ${
                                period === p.id ? 'bg-[#FFD700] text-black' : 'text-[#A0AAB2] hover:text-white'
                            }`}
                            data-testid={`top-consumers-period-${p.id}`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>
            <p className="text-[10px] text-[#A0AAB2] mb-3">
                Players ranked by free-play + playback received this period. Use this to see who&apos;s using your customer-service tools the most — no judgment, just visibility.
            </p>
            {loading && rows.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-[#FFD700] animate-spin" />
                </div>
            ) : rows.length === 0 ? (
                <p className="text-center text-[#A0AAB2] py-8 text-sm">No promo or playback activity this period.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-[10px] text-[#A0AAB2] uppercase tracking-widest">
                                <th className="px-2 py-1.5">#</th>
                                <th className="px-2 py-1.5">Player</th>
                                <th className="px-2 py-1.5 text-right">Promo</th>
                                <th className="px-2 py-1.5 text-right">Playbacks</th>
                                <th className="px-2 py-1.5 text-right">Combined</th>
                                <th className="px-2 py-1.5 text-right">Risk ratio</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={r.user_id} className="border-t border-white/5" data-testid={`top-consumer-${r.user_id}`}>
                                    <td className="px-2 py-2 text-[#666] font-mono">{i + 1}</td>
                                    <td className="px-2 py-2">
                                        <div className="flex items-center gap-2">
                                            <PlayerNameLink id={r.user_id} name={r.name} testId={`top-consumer-name-${i}`} />
                                        </div>
                                        <div className="text-[10px] text-[#666]">{r.email}</div>
                                    </td>
                                    <td className="px-2 py-2 text-right font-mono text-[#FFD700]">
                                        ${r.promo_total.toFixed(2)}
                                        <span className="block text-[9px] text-[#666]">{r.promo_count} tx</span>
                                    </td>
                                    <td className="px-2 py-2 text-right font-mono text-[#A0AAB2]">
                                        ${r.playback_total.toFixed(2)}
                                        <span className="block text-[9px] text-[#666]">{r.playback_count} tx</span>
                                    </td>
                                    <td className="px-2 py-2 text-right font-mono font-black text-white">
                                        ${r.combined.toFixed(2)}
                                    </td>
                                    <td className="px-2 py-2 text-right font-mono font-bold text-[#A0AAB2]">
                                        {(r.playback_ratio * 100).toFixed(0)}%
                                        <span className="block text-[9px] text-[#666]">
                                            ${r.lifetime_playbacks.toFixed(0)} / ${r.lifetime_loads.toFixed(0)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default TopCreditConsumersCard;
